import {
  ArrowUpIcon,
  DocumentTextIcon,
  EyeIcon,
  PhotoIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { CodeBracketIcon } from '@heroicons/react/24/solid';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { AnimatePresence, motion } from 'framer-motion';
import { analyticsService } from 'src/services/analytics/analyticsService';
import {
  RFQ_BUILDER_ADD_REFERENCES_OPENED,
  RFQ_BUILDER_MESSAGE_SENT
} from 'src/services/analytics/rfqBuilderEvents';
import { useMobileKeyboard } from 'src/shared/hooks';
import { Rfq } from 'src/shared/types/rfq';
import { UploadedFile } from 'src/shared/types/uploadedFile';

import { cn, Progress, Spinner } from 'src/shared/components/ui';
import { ButtonV2, Textarea, Typography } from 'src/shared/components/ui';
import { isWebGLAvailable } from 'src/shared/components/WebGLErrorBoundary';

import {
  Message,
  useRfqConversationContext
} from '../context/RfqConversationContext';
import useConversationHistory from '../hooks/useConversationHistory';
import BlobLoader from './BlobLoader';
import BlobV2 from './BlobV2';
import RfqChatReceived from './rfq-chat/RfqChatReceived';
import RfqChatSent from './rfq-chat/RfqChatSent';
import AddReferencesModal from './rfq-files/AddReferencesModal';
import { MediaCarouselModal, MediaItem } from './rfq-files/MediaCarouselModal';

interface RfqBuilderChatProps {
  isGeneratingRfq: boolean;
  isDrawerOpen: boolean;
  rfq?: Rfq;
  /** Closes the drawer when a message is submitted while it is open. */
  handleDrawerToggle: () => void;
  /** Explicitly opens the drawer in review mode (eye icon click). */
  onReviewRfq: () => void;
  onFinishChat: () => void;
}

const RfqBuilderChat = ({
  isDrawerOpen,
  rfq,
  handleDrawerToggle,
  onReviewRfq,
  onFinishChat
}: RfqBuilderChatProps) => {
  const queryClient = useQueryClient();

  // Refetch the RFQ query so the parent page has fresh data
  useEffect(() => {
    if (rfq?.rfq?.id) {
      queryClient.invalidateQueries({
        queryKey: ['rfq', rfq.rfq.id.toString()]
      });
    }
  }, [rfq?.rfq?.id, queryClient]);

  // Use shared conversation context
  const { messages, setMessages, isLoading, isSending, connectToConversation } =
    useRfqConversationContext();

  const [inputValue, setInputValue] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [showAddReferences, setShowAddReferences] = useState(false);
  // Files forwarded from window-level drag-and-drop into the AddReferences modal.
  // Cleared by the modal via onInitialFilesConsumed once the upload tab seeds them.
  const [droppedFiles, setDroppedFiles] = useState<File[] | undefined>(
    undefined
  );
  const [completionValue, setCompletionValue] = useState<number | undefined>(
    undefined
  );
  const scrollableContainerRef = useRef<HTMLElement | null>(null);
  const hasInitializedRef = useRef(false);
  const textareaWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragCounterRef = useRef(0);

  // Media viewer modal state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  // Compute all media (images and files) from all messages (deduplicated by id)
  const allMedia = useMemo(() => {
    const media: MediaItem[] = [];
    const seenIds = new Set<string>();

    messages.forEach((message) => {
      if (message.files) {
        message.files.forEach((file) => {
          const fileId = file.id.toString();

          if (!seenIds.has(fileId)) {
            seenIds.add(fileId);
            media.push({
              id: fileId,
              url: file.url,
              name: file.name,
              content_type: file.content_type,
              caption: file.description ?? undefined
            });
          }
        });
      }
    });

    return media;
  }, [messages]);

  const { data: conversationHistory, isFetching: isFetchingHistory } =
    useConversationHistory(rfq?.rfq?.id);

  const isKeyboardVisible = useMobileKeyboard(0.2); // 20% threshold

  // Find and store the scrollable container ref
  useEffect(() => {
    if (containerRef.current) {
      // Find the closest scrollable parent
      let scrollableParent: HTMLElement | null =
        containerRef.current.parentElement;

      while (scrollableParent) {
        const overflowY = window.getComputedStyle(scrollableParent).overflowY;

        if (overflowY === 'auto' || overflowY === 'scroll') {
          scrollableContainerRef.current = scrollableParent;

          return;
        }
        scrollableParent = scrollableParent.parentElement;
      }
    }
  }, []);

  const scrollToBottom = useCallback(
    (useInstant = false) => {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        setTimeout(
          () => {
            // Find the scrollable container (the fixed parent with overflow-y-auto)
            if (containerRef.current && messages.length > 0) {
              let scrollableParent: HTMLElement | null =
                containerRef.current.parentElement;

              while (scrollableParent) {
                const overflowY =
                  window.getComputedStyle(scrollableParent).overflowY;

                if (overflowY === 'auto' || overflowY === 'scroll') {
                  scrollableParent.scrollTo({
                    top: scrollableParent.scrollHeight,
                    behavior: useInstant ? 'auto' : 'smooth'
                  });

                  return;
                }
                scrollableParent = scrollableParent.parentElement;
              }
            }
          },
          useInstant ? 0 : 100
        );
      });
    },
    [messages.length]
  );

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Handle Android keyboard opening - maintain scroll at bottom
  // Combined handler using both isKeyboardVisible and visualViewport for better coverage
  useEffect(() => {
    if (messages.length === 0) return;

    let viewportResizeTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleKeyboardOpen = () => {
      // Debounce to prevent excessive scroll calls
      if (viewportResizeTimeout) {
        clearTimeout(viewportResizeTimeout);
      }

      viewportResizeTimeout = setTimeout(() => {
        scrollToBottom(true);
      }, 100);
    };

    // Use visualViewport API if available (more reliable on Android)
    if (typeof window !== 'undefined' && window.visualViewport) {
      const handleViewportResize = () => {
        const viewport = window.visualViewport;

        if (!viewport) return;

        // If viewport height is significantly reduced, keyboard likely opened
        const heightReduction =
          (window.innerHeight - viewport.height) / window.innerHeight;

        if (heightReduction > 0.2) {
          handleKeyboardOpen();
        }
      };

      window.visualViewport.addEventListener('resize', handleViewportResize);

      return () => {
        if (viewportResizeTimeout) {
          clearTimeout(viewportResizeTimeout);
        }
        if (window.visualViewport) {
          window.visualViewport.removeEventListener(
            'resize',
            handleViewportResize
          );
        }
      };
    } else {
      // Fallback: use isKeyboardVisible hook
      if (isKeyboardVisible) {
        handleKeyboardOpen();
      }

      return () => {
        if (viewportResizeTimeout) {
          clearTimeout(viewportResizeTimeout);
        }
      };
    }
  }, [isKeyboardVisible, messages.length, scrollToBottom]);

  // Focus textarea when LLM finishes replying
  useEffect(() => {
    if (!isLoading) {
      const textareaElement =
        textareaWrapperRef.current?.querySelector('textarea');

      textareaElement?.focus();
    }
  }, [isLoading]);

  // Load conversation history into messages when data is fetched
  useEffect(() => {
    if (hasInitializedRef.current) return;

    // If an SSE connection is already in flight (e.g. from the file picking
    // screens that navigate here mid-stream), the messages are already being
    // populated via the shared context — mark as initialized and skip.
    if (isLoading || isSending) {
      hasInitializedRef.current = true;

      return;
    }

    // Wait until fetching is complete to ensure we have fresh data
    if (!conversationHistory || isFetchingHistory) return;

    hasInitializedRef.current = true;

    if (conversationHistory.turns.length > 0) {
      // Load existing conversation history
      const historicalMessages: Message[] = conversationHistory.turns.map(
        (turn) => ({
          id: turn.id,
          text: turn.text,
          isUser: turn.turn_type === 'text' || turn.turn_type === 'file',
          timestamp: new Date(turn.created_at),
          files: turn.files
        })
      );
      const reversedMessages = historicalMessages.reverse().slice(1);

      if (reversedMessages.length > 0) {
        setMessages(reversedMessages);
      }
    } else if (rfq?.rfq?.document?.name) {
      const firstMessage = `Let's get started making an RFQ for a ${rfq.rfq.document.name}`;

      connectToConversation(firstMessage);
    }
  }, [
    conversationHistory,
    rfq?.rfq?.document?.name,
    connectToConversation,
    isFetchingHistory,
    isLoading,
    isSending
  ]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    if (!rfq?.rfq?.id) return;

    analyticsService.trackEvent(RFQ_BUILDER_MESSAGE_SENT, {
      rfq_id: rfq.rfq.id,
      message_length: inputValue.trim().length,
      has_attachments: false,
      attachment_count: 0
    });

    const messageToSend = inputValue;

    setInputValue('');

    // Close the RFQ drawer on submission if it's open
    if (isDrawerOpen) {
      handleDrawerToggle();
    }

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        text: messageToSend,
        isUser: true,
        timestamp: new Date()
      }
    ]);
    await connectToConversation(messageToSend);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading && !isSending) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Window-level drag and drop handlers
  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current++;
      if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;

      const files = e.dataTransfer?.files;

      if (!files || files.length === 0) return;

      const accepted = Array.from(files);

      if (accepted.length === 0) return;

      setDroppedFiles(accepted);
      setShowAddReferences(true);
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, []);

  const handleInitialFilesConsumed = useCallback(() => {
    setDroppedFiles(undefined);
  }, []);

  const handleImageClick = useCallback(
    (clickedImageId: string) => {
      const clickedIndex = allMedia.findIndex(
        (img) => img.id === clickedImageId
      );

      if (clickedIndex >= 0) {
        setViewerIndex(clickedIndex);
        setViewerOpen(true);
      }
    },
    [allMedia]
  );

  const handleViewerClose = useCallback(() => {
    setViewerOpen(false);
  }, []);

  const handleViewerNext = useCallback(() => {
    // Wrap around to first item if at the end
    const nextIndex = viewerIndex >= allMedia.length - 1 ? 0 : viewerIndex + 1;

    setViewerIndex(nextIndex);
  }, [viewerIndex, allMedia.length]);

  const handleViewerPrevious = useCallback(() => {
    // Wrap around to last item if at the beginning
    const prevIndex = viewerIndex <= 0 ? allMedia.length - 1 : viewerIndex - 1;

    setViewerIndex(prevIndex);
  }, [viewerIndex, allMedia.length]);

  const handleViewerIndexChange = useCallback((index: number) => {
    setViewerIndex(index);
  }, []);

  const handleSignalDetected = useCallback((signal: string) => {
    // Extract completion value from signals like "completion 0.24"
    if (signal.startsWith('completion ')) {
      const completionNumber = parseFloat(signal.replace('completion ', ''));

      if (!isNaN(completionNumber)) {
        // Convert to percentage (0.24 -> 24, 1 -> 100)
        setCompletionValue(completionNumber * 100);
      }
    }
  }, []);

  const handleImagesSelected = useCallback(
    async (files: UploadedFile[]) => {
      if (files.length === 0) return;

      const fileIds = files.map((f) => f.id.toString());

      // Add user message with the files
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          text: '',
          isUser: true,
          timestamp: new Date(),
          files
        }
      ]);

      // Send to conversation with file IDs
      await connectToConversation('', fileIds);
    },
    [setMessages, connectToConversation]
  );

  // Full-screen drop zone overlay (rendered via portal to cover entire page)
  const dropZoneOverlay = isDragging
    ? createPortal(
        <div className="fixed inset-0 z-[9999] bg-gradient-to-b from-white/70 via-white/85 to-white/70 backdrop-blur-xl flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-4">
            {/* Decorative file icons */}
            <div className="flex items-end gap-1 mb-2">
              <div className="bg-gray-100 rounded-lg p-2 -rotate-12 shadow-lg">
                <CodeBracketIcon className="size-8 text-gray-500" />
              </div>
              <div className="bg-primary rounded-xl p-3 z-10 shadow-lg">
                <PhotoIcon className="size-10 text-white" />
              </div>
              <div className="bg-blue-400 rounded-lg p-2 rotate-12 shadow-lg">
                <DocumentTextIcon className="size-8 text-white" />
              </div>
            </div>

            <Typography variant="h3" weight="bold" className="text-gray-900">
              Add anything
            </Typography>
            <Typography variant="body-1" className="text-gray-500">
              Drop any file here to add it to the conversation
            </Typography>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={containerRef} className="flex flex-col relative">
      {/* chat header */}
      {dropZoneOverlay}
      <div className="sticky top-0 pt-[64px] w-full z-30 bg-white flex-shrink-0 rounded-b-xl">
        <div className="absolute -bottom-10 left-0 right-0 h-12 bg-gradient-to-b from-white to-transparent pointer-events-none" />
        <div className="px-6 rounded-xl border border-gray-4 flex flex-col h-16 relative justify-center shadow-[0_2px_2px_0_rgba(0,0,0,0.10)] gap-2">
          <div className="flex items-center justify-between gap-2">
            <Typography variant="h7" weight="bold">
              {rfq?.rfq?.document?.name || 'Product Specification Document'}
            </Typography>
            <AnimatePresence mode="wait">
              {isLoading ? (
                <>
                  <motion.div
                    className={cn(
                      'absolute pointer-events-none overflow-hidden flex items-center justify-center',
                      isWebGLAvailable()
                        ? 'right-0 bottom-3'
                        : 'right-6 bottom-[55%] translate-y-1/2'
                    )}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    style={{ willChange: 'transform' }}
                  >
                    {isWebGLAvailable() ? (
                      <BlobLoader
                        width={80}
                        height={56}
                        numBlobs={3}
                        speed={0.5}
                      />
                    ) : (
                      <Spinner size="sm" />
                    )}
                  </motion.div>
                  <div className="size-6" />
                </>
              ) : (
                <motion.div
                  key="button"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div onClick={onReviewRfq} className="cursor-pointer">
                    <EyeIcon className="size-6 text-primary" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Progress
            aria-label="Loading..."
            size="sm"
            value={completionValue}
            classNames={{ track: 'h-[2px]' }}
          />
        </div>
      </div>

      {/* Messages Area - Natural page flow */}
      <div
        className="py-6 w-full flex-1 flex flex-col"
        style={{
          minHeight: 'calc(100dvh - 305px)'
        }}
      >
        {messages.length === 0 ? (
          <div className="flex items-center justify-center text-center relative flex-1">
            <BlobV2
              key="blob-empty-state"
              numBlobs={6}
              isolation={50}
              lookAt={{ x: 0, y: 0, z: 0 }}
              zoom={1}
            />
          </div>
        ) : (
          <div
            className={cn(
              'relative flex flex-col gap-8 flex-1 pb-6',
              isKeyboardVisible ? 'justify-end' : ''
            )}
          >
            <AnimatePresence initial={false}>
              {messages.map((message) =>
                message.isUser ? (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: 'easeInOut' }}
                  >
                    <RfqChatSent
                      text={message.text}
                      files={message.files}
                      onImageClick={handleImageClick}
                    />
                  </motion.div>
                ) : (
                  <RfqChatReceived
                    key={message.id}
                    text={message.text}
                    onFinishChat={onFinishChat}
                    onSignalDetected={handleSignalDetected}
                  />
                )
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* chat footer - input area */}
      <div
        className={cn(
          'sticky mt-auto bottom-0 left-0 right-0 w-full z-30 bg-white',
          isKeyboardVisible ? 'pb-2' : 'pb-6'
        )}
      >
        {/* Fade overlay - creates fade-out effect for messages above */}
        <div className="absolute -top-12 left-0 right-0 h-12 bg-gradient-to-b from-transparent to-white pointer-events-none" />

        <div className="relative bg-gray-1 rounded-xl" ref={textareaWrapperRef}>
          <Textarea
            autoFocus
            classNames={{
              input: '!bg-gray-1',
              inputWrapper:
                '!bg-gray-1 focus-within:!bg-gray-1 focus:!bg-gray-1 hover:!bg-gray-1 !border-gray-1 rounded-xl',
              mainWrapper: '!bg-gray-1',
              innerWrapper: 'items-end'
            }}
            value={inputValue}
            onValueChange={setInputValue}
            onKeyDown={handleKeyPress}
            onFocus={() => {
              // When textarea is focused, ensure we're scrolled to bottom
              // This helps prevent Android's automatic scroll-into-view from jumping
              if (messages.length > 0) {
                // Use a small delay to let the browser's scroll happen first,
                // then override it by scrolling to bottom
                setTimeout(() => {
                  scrollToBottom(true);
                }, 100);
              }
            }}
            placeholder="Describe what you want to build"
            minRows={5}
            maxRows={5}
            isDisabled={isLoading || isSending}
            endContent={
              <div className="flex flex-col justify-start">
                <ButtonV2
                  isIconOnly
                  color="primary"
                  onPress={handleSendMessage}
                  isDisabled={!inputValue.trim() || isLoading || isSending}
                  isLoading={isLoading || isSending}
                  className="mt-auto"
                  aria-label="Send message"
                  radius="full"
                  size="md"
                >
                  <ArrowUpIcon className="size-4" />
                </ButtonV2>
              </div>
            }
          />
          <div className="absolute bottom-3 left-3 flex gap-2">
            <ButtonV2
              color="fourth"
              onPress={() => {
                analyticsService.trackEvent(RFQ_BUILDER_ADD_REFERENCES_OPENED, {
                  rfq_id: rfq?.rfq?.id
                });
                setShowAddReferences(true);
              }}
              isDisabled={isLoading || isSending}
              className="px-3 !font-semibold"
              aria-label="Add references"
              size="sm"
              radius="full"
            >
              <SparklesIcon className="size-3" /> Add references
            </ButtonV2>
          </div>
        </div>
      </div>

      {/* Media Viewer Modal */}
      {allMedia.length > 0 && (
        <MediaCarouselModal
          isOpen={viewerOpen}
          onClose={handleViewerClose}
          items={allMedia}
          currentIndex={viewerIndex}
          onNext={handleViewerNext}
          onPrevious={handleViewerPrevious}
          onIndexChange={handleViewerIndexChange}
        />
      )}

      {/* Add References Modal */}
      <AddReferencesModal
        isOpen={showAddReferences}
        onClose={() => setShowAddReferences(false)}
        rfq={rfq}
        onFilesSelected={handleImagesSelected}
        initialFiles={droppedFiles}
        onInitialFilesConsumed={handleInitialFilesConsumed}
      />
    </div>
  );
};

export default RfqBuilderChat;
